// Extracted from original/src/components/InstallmentsView.tsx (ADR-08 §2.1 "copy, then cut logic"):
//   new-contract form: customer (new / existing, same-name block), item type (store product / bank cards / dollar and
//   currency vaults), contract currency, price terms, down payment and its box, frequency, count, company deduction,
//   first due date, calculation box, note, schedule preview, submit                       :1527-2456
//   contract detail modal: header with capsule, "إعادة توزيع الأقساط المتبقية", delete, info cards, schedule rows with the
//   inline amount edit and "تسديد القسط"                                                  :2862-3067
//   payment modal: row info, amount, box, note                                             :3069-3215
//   delete confirmation modal (frame)                                                      :3218-3305
// Kept: every class string, icon, label order, the customer-mode toggle, the item-type tiles, the purple / blue panels,
// the currency toggle, the indigo price box with its profit lines, the emerald down-payment panel, the dark calculation
// box with its four figures, the schedule preview list, the submit / cancel buttons, the detail modal's cards and rows,
// the payment modal's frame and fields, the delete modal's rose frame and warning box.
// Cut: every figure the old form computed in the browser (`percentageProfitAmount`, `totalPrice`, `remainingBalance`,
// `installmentAmount`, `netProfit` clamped at 0, the UTC `previewSchedule`, R-1 / R-2 / AL / AN) → the server's preview
// (`POST /installments/preview`: `derived` + `schedule`, D-106, D-107), passed in as labels (`figures`); the
// browser-side duplicate check (`checkCustomerDuplicate`) → the API's `CUSTOMER_NAME_EXISTS` matches (D-017, D-245 (b));
// the redistribution estimate of the payment modal (`nextEst`, R-7) → the server's preview of the payment
// (`remaining_after`, D-108: payments apply to rows oldest first, nothing is redistributed); the super-code input of the
// delete modal (R-9, "2" always accepted, F-157) → a written reason, then the personal PIN dialog (D-109 (4), D-018).
// Changed by decision: an item kept outside the store is a contract with a typed item name (`kind: none`, D-291 (b));
// a product item names product × warehouse with its stock (D-110, D-292); a currency item from the currency vault is an
// IQD contract at the approved sell rate (D-293, D-105); the deduction is shown as its own negative line and the net
// profit is not clamped (D-106, D-015); a row shows "partially paid" with the amount left (D-108); the edit applies to
// unpaid rows only and keeps the unpaid rows equal to the remaining (D-109 (3)); the detail lists the contract's payments
// with their cancel (each payment on its own, D-109 (4)) and receipt print (D-108).
// Added: `ReasonDialog` (the C24 cancel-dialog pattern in the delete modal's frame) and data-testid attributes.
import { useState, type FormEvent, type ReactNode } from 'react';
import {
  AlertTriangle, Calculator, Calendar, Check, CheckCircle2, Coins, CreditCard, DollarSign, Edit2, Landmark, Package, Plus, Printer,
  RefreshCw, ShieldAlert, Sparkles, Trash2, User, X,
} from 'lucide-react';
import { CustomSelect, type SelectOption } from '../components/CustomSelect.js';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';
import { useI18n } from '../i18n-context.js';
import { toEnglishDigits } from '../lib/format.js';
import { PrintShareCapsule } from './Installments.js';

/** Old `formatInputValue` (IV:18-25): thousands separators on a typed decimal. */
export const formatInputValue = (val: string): string => {
  if (val === '') return '';
  const numStr = toEnglishDigits(String(val)).replace(/,/g, '');
  if (numStr === '') return '';
  const parts = numStr.split('.');
  const intFormatted = (parts[0] ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.length > 1 ? `${intFormatted}.${parts[1]}` : intFormatted;
};

/** The old onChange cleaning of the money inputs (IV:1846-1857 …): digits and one dot. */
export const cleanDecimal = (v: string): string => {
  const raw = toEnglishDigits(v).replace(/,/g, '').replace(/[^0-9.]/g, '');
  if (raw === '') return '';
  const parts = raw.split('.');
  return (parts[0] ?? '') + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
};

// ---------------------------------------------------------------------------
// New contract form
// ---------------------------------------------------------------------------

export type ContractItemType = 'product' | 'crypto' | 'foreign_currency';
export type ContractPeriod = 'monthly' | 'weekly' | 'daily';

export interface ContractFormValues {
  readonly customerMode: 'new' | 'existing';
  /** Existing: the customer row (account) id. */
  readonly customerRowId: string | null;
  readonly name: string;
  readonly phone: string;
  readonly address: string;
  readonly itemType: ContractItemType;
  /** "<productId>:<warehouseId>" or null (no product: a typed item kept outside the store). */
  readonly productKey: string | null;
  readonly cardBoxId: string | null;
  readonly cardAmount: string;
  readonly vaultSource: 'dollar_vault' | 'currency_vault';
  readonly currencyCode: string;
  readonly vaultAmount: string;
  readonly fxAmount: string;
  readonly itemName: string;
  readonly currency: 'IQD' | 'USD';
  readonly basePrice: string;
  readonly profitPct: string;
  readonly downPayment: string;
  readonly downPaymentAccountId: string | null;
  readonly period: ContractPeriod;
  readonly count: string;
  readonly deductionPct: string;
  readonly firstDueDate: string;
  readonly note: string;
}

export interface ContractProductOption {
  readonly key: string;
  readonly name: string;
  readonly priceLabel: string;
  readonly stockLabel: string;
  readonly warehouseName: string;
  /** Pre-fill candidates (old "خيارات التسعير"): the average cost and the sale price, whole IQD strings or null. */
  readonly costPrice: string | null;
  readonly salePrice: string | null;
}

export interface ContractCurrencyOption {
  readonly code: string;
  readonly nameAr: string;
  readonly symbol: string;
  readonly balanceLabel: string;
  /** "1 ₺ = 48 د.ع" — the approved sell rate in its quote unit (D-021, D-105). */
  readonly rateLabel: string;
}

/** Server figures of the last preview (labels with the currency), null while none. */
export interface ContractFigures {
  readonly totalLabel: string;
  readonly percentageLabel: string;
  /** Product with cost: the extra profit above cost (profit − percentage); null otherwise. */
  readonly additionalLabel: string | null;
  readonly profitLabel: string;
  readonly costLabel: string | null;
  readonly profitVsCostLabel: string | null;
  readonly remainingLabel: string;
  readonly installmentLabel: string;
  readonly netProfitLabel: string;
  readonly netNegative: boolean;
  readonly deductionLabel: string;
  readonly schedule: ReadonlyArray<{ readonly no: number; readonly dueDate: string; readonly amountLabel: string }>;
}

export interface ContractDuplicate {
  readonly name: string;
  readonly rowId: string;
}

export interface NewContractFormProps {
  readonly values: ContractFormValues;
  readonly onChange: (patch: Partial<ContractFormValues>) => void;
  readonly customers: readonly SelectOption[];
  readonly onSelectCustomer: (rowId: string) => void;
  readonly duplicate: ContractDuplicate | null;
  readonly onUseDuplicate: () => void;
  readonly products: readonly ContractProductOption[];
  readonly storeOn: boolean;
  readonly cardBoxes: readonly SelectOption[];
  readonly cardBoxInfo: { readonly name: string; readonly iqdLabel: string } | null;
  readonly dollarVault: { readonly name: string; readonly balanceLabel: string } | null;
  readonly currencies: readonly ContractCurrencyOption[];
  readonly downPaymentBoxes: readonly SelectOption[];
  readonly figures: ContractFigures | null;
  /** Preview refusal (translated), shown above the calculation box. */
  readonly previewError: string | null;
  readonly previewing: boolean;
  readonly busy: boolean;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
}

const inputCls = 'w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:border-blue-500';
const labelCls = 'block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5';

export function NewContractForm(p: NewContractFormProps) {
  const { t } = useI18n();
  const v = p.values;
  const cur = v.currency;
  const selectedProduct = p.products.find((x) => x.key === v.productKey) ?? null;
  const selectedFx = p.currencies.find((c) => c.code === v.currencyCode) ?? null;
  const hasDown = v.downPayment !== '' && Number(v.downPayment) > 0;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    p.onSubmit();
  };
  // the old auto-fill of the item text (IV:497-555): labels only, no money
  const cardText = (boxName: string | undefined, amount: string) => t('in.item_card_text', { box: boxName ?? '', qty: amount }).replace(/ - $/, '');
  const fxText = (code: string, amount: string) => {
    const c = p.currencies.find((x) => x.code === code);
    return t('in.item_fx_text', { name: c?.nameAr ?? code, qty: amount, symbol: c?.symbol ?? '' });
  };

  return (
    <form onSubmit={submit} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm space-y-6" data-testid="in-new-form">
      <div className="flex items-center gap-3 pb-4 border-b border-gray-100 dark:border-gray-800">
        <div className="p-3 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 rounded-xl">
          <Plus size={22} />
        </div>
        <div>
          <h2 className="text-lg font-black text-gray-900 dark:text-white">{t('in.form_title')}</h2>
          <p className="text-xs text-gray-500">{t('in.form_sub')}</p>
        </div>
      </div>

      {/* SECTION 1: CUSTOMER DETAILS */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-black text-gray-800 dark:text-gray-200 flex items-center gap-2">
            <User size={16} className="text-blue-500" /> {t('in.customer_section')}
          </h3>
          <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg text-xs font-bold">
            <button
              type="button"
              onClick={() => p.onChange({ customerMode: 'new', customerRowId: null, name: '' })}
              className={`px-3 py-1.5 rounded-md transition-all cursor-pointer ${v.customerMode === 'new' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}
            >
              {t('in.customer_new')}
            </button>
            <button
              type="button"
              onClick={() => p.onChange({ customerMode: 'existing' })}
              data-testid="in-customer-existing"
              className={`px-3 py-1.5 rounded-md transition-all cursor-pointer ${v.customerMode === 'existing' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}
            >
              {t('in.customer_existing')}
            </button>
          </div>
        </div>

        {v.customerMode === 'existing' && (
          <div>
            <label className={labelCls}>{t('in.customer_pick_label')}</label>
            <CustomSelect options={[...p.customers]} value={v.customerRowId ?? ''} onChange={(id) => p.onSelectCustomer(String(id))} placeholder={t('in.customer_pick_ph')} searchable={true} />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <label className={labelCls}>
              {t('in.customer_name')} <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              value={v.name}
              readOnly={v.customerMode === 'existing'}
              onChange={(e) => p.onChange({ name: e.target.value })}
              placeholder={t('in.customer_name_ph')}
              data-testid="in-customer-name"
              className={`w-full bg-gray-50 dark:bg-gray-800 border rounded-xl px-4 py-3 text-sm font-bold text-gray-900 dark:text-white focus:outline-none ${
                p.duplicate ? 'border-amber-500 ring-2 ring-amber-500/20' : 'border-gray-200 dark:border-gray-700 focus:border-blue-500'
              }`}
            />
          </div>
          <div>
            <label className={labelCls}>{t('in.customer_phone')}</label>
            <input type="text" dir="ltr" value={v.phone} readOnly={v.customerMode === 'existing'} onChange={(e) => p.onChange({ phone: e.target.value })} placeholder="0770xxxxxxx" className={`${inputCls} font-mono text-left`} />
          </div>
          <div>
            <label className={labelCls}>{t('in.customer_address')}</label>
            <input type="text" value={v.address} readOnly={v.customerMode === 'existing'} onChange={(e) => p.onChange({ address: e.target.value })} placeholder={t('in.customer_address_ph')} className={inputCls} />
          </div>
        </div>

        {v.customerMode === 'new' && p.duplicate && (
          <div className="bg-amber-50 dark:bg-amber-950/60 border-2 border-amber-400 rounded-xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 shadow-sm animate-fade-in" data-testid="in-duplicate">
            <div className="flex items-start gap-3">
              <ShieldAlert size={22} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-xs font-black text-amber-800 dark:text-amber-300">{t('in.duplicate_title', { name: p.duplicate.name })}</h4>
                <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">{t('in.duplicate_body')}</p>
              </div>
            </div>
            <button type="button" onClick={p.onUseDuplicate} className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-black px-4 py-2 rounded-lg transition-all shrink-0 cursor-pointer shadow-sm">
              {t('in.duplicate_use')}
            </button>
          </div>
        )}
      </div>

      {/* SECTION 2: ITEM / PRODUCT SELECTION */}
      <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-800">
        <h3 className="text-sm font-black text-gray-800 dark:text-gray-200 flex items-center gap-2">
          <Package size={16} className="text-emerald-500" /> {t('in.item_section')}
        </h3>
        <div className="grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => p.onChange({ itemType: 'product' })}
            data-testid="in-item-product"
            className={`p-3 rounded-xl border text-center font-bold text-xs transition-all flex flex-col items-center gap-2 cursor-pointer ${
              v.itemType === 'product' ? 'bg-emerald-50 dark:bg-emerald-950/60 border-emerald-500 text-emerald-700 dark:text-emerald-300 ring-2 ring-emerald-500/20' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <Package size={20} />
            <span>{t('in.item_type_product')}</span>
          </button>
          <button
            type="button"
            onClick={() => p.onChange({ itemType: 'crypto', currency: 'IQD' })}
            data-testid="in-item-card"
            className={`p-3 rounded-xl border text-center font-bold text-xs transition-all flex flex-col items-center gap-2 cursor-pointer ${
              v.itemType === 'crypto' ? 'bg-purple-50 dark:bg-purple-950/60 border-purple-500 text-purple-700 dark:text-purple-300 ring-2 ring-purple-500/20' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <Coins size={20} />
            <span>{t('in.item_type_card')}</span>
          </button>
          <button
            type="button"
            onClick={() => p.onChange({ itemType: 'foreign_currency', ...(v.vaultSource === 'dollar_vault' ? { currency: 'USD' as const } : { currency: 'IQD' as const }) })}
            data-testid="in-item-currency"
            className={`p-3 rounded-xl border text-center font-bold text-xs transition-all flex flex-col items-center gap-2 cursor-pointer ${
              v.itemType === 'foreign_currency' ? 'bg-blue-50 dark:bg-blue-950/60 border-blue-500 text-blue-700 dark:text-blue-300 ring-2 ring-blue-500/20' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <Landmark size={20} />
            <span>{t('in.item_type_vaults')}</span>
          </button>
        </div>

        {v.itemType === 'product' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t('in.product_pick_label')}</label>
              {p.storeOn ? (
                <CustomSelect
                  options={p.products.map((x) => ({ id: x.key, label: `${x.name} — ${x.warehouseName}`, subLabel: x.priceLabel, badge: x.stockLabel }))}
                  value={v.productKey ?? ''}
                  onChange={(key) => {
                    const prod = p.products.find((x) => x.key === String(key));
                    p.onChange({ productKey: String(key), currency: 'IQD', ...(prod ? { itemName: prod.name, basePrice: prod.costPrice ?? prod.salePrice ?? '' } : {}) });
                  }}
                  placeholder={t('in.product_pick_ph')}
                  searchable={true}
                />
              ) : (
                <div className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-xs font-bold text-gray-500">{t('in.store_off')}</div>
              )}
            </div>
            <div>
              <label className={labelCls}>{t('in.product_name_label')}</label>
              <input type="text" required value={v.itemName} onChange={(e) => p.onChange({ itemName: e.target.value })} placeholder={t('in.product_name_ph')} data-testid="in-item-name" className={inputCls} />
            </div>
          </div>
        ) : v.itemType === 'crypto' ? (
          <div className="space-y-4 bg-purple-50/50 dark:bg-purple-950/20 p-4 rounded-2xl border border-purple-100 dark:border-purple-900/40">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>{t('in.card_pick_label')}</label>
                <CustomSelect
                  options={[...p.cardBoxes]}
                  value={v.cardBoxId ?? ''}
                  onChange={(id) => {
                    const box = p.cardBoxes.find((b) => String(b.id) === String(id));
                    p.onChange({ cardBoxId: String(id), itemName: cardText(box?.label, v.cardAmount) });
                  }}
                  placeholder={t('in.card_pick_ph')}
                  searchable={true}
                />
              </div>
              <div>
                <label className={labelCls}>{t('in.card_amount_label')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  dir="ltr"
                  lang="en-US"
                  value={formatInputValue(v.cardAmount)}
                  onChange={(e) => {
                    const val = cleanDecimal(e.target.value);
                    const box = p.cardBoxes.find((b) => String(b.id) === v.cardBoxId);
                    p.onChange({ cardAmount: val, itemName: cardText(box?.label, val), ...(val !== '' ? { basePrice: val } : {}) });
                  }}
                  placeholder="500"
                  className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:border-purple-500 font-mono text-left"
                />
              </div>
            </div>
            {p.cardBoxInfo && (
              <div className="bg-white dark:bg-gray-800/90 p-3.5 rounded-xl border border-purple-200 dark:border-purple-800 flex items-center justify-between text-xs shadow-sm">
                <div className="flex items-center gap-2">
                  <Coins size={18} className="text-purple-600 dark:text-purple-400" />
                  <span className="font-bold text-gray-800 dark:text-gray-200">{t('in.card_selected', { name: p.cardBoxInfo.name })}</span>
                </div>
                <div className="flex items-center gap-3 font-mono font-bold">
                  <span className="text-emerald-600 dark:text-emerald-400">{t('in.card_iqd_balance', { v: p.cardBoxInfo.iqdLabel })}</span>
                </div>
              </div>
            )}
            <div>
              <label className={labelCls}>{t('in.card_name_label')}</label>
              <input type="text" required value={v.itemName} onChange={(e) => p.onChange({ itemName: e.target.value })} placeholder={t('in.card_name_ph')} className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:border-purple-500" />
            </div>
          </div>
        ) : (
          <div className="space-y-4 bg-blue-50/50 dark:bg-blue-950/20 p-4 rounded-2xl border border-blue-100 dark:border-blue-900/40">
            <div className="flex items-center gap-2 p-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => p.onChange({ vaultSource: 'dollar_vault', currency: 'USD', itemName: t('in.item_usd_text', { qty: v.vaultAmount }), ...(v.vaultAmount !== '' ? { basePrice: v.vaultAmount } : {}) })}
                className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${v.vaultSource === 'dollar_vault' ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                <DollarSign size={16} />
                <span>{t('in.vault_dollar_tab', { v: p.dollarVault?.balanceLabel ?? '0' })}</span>
              </button>
              <button
                type="button"
                onClick={() => p.onChange({ vaultSource: 'currency_vault', currency: 'IQD', itemName: fxText(v.currencyCode, v.fxAmount) })}
                className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${v.vaultSource === 'currency_vault' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                <Landmark size={16} />
                <span>{t('in.vault_currency_tab')}</span>
              </button>
            </div>

            {v.vaultSource === 'dollar_vault' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>{t('in.vault_selected_label')}</label>
                    <div className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 dark:text-white flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Landmark size={18} className="text-emerald-500" />
                        {p.dollarVault?.name ?? t('in.dollar_vault_name')}
                      </span>
                      <span className="text-xs font-mono font-black text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2.5 py-1 rounded-lg">{t('in.balance_usd', { v: p.dollarVault?.balanceLabel ?? '0' })}</span>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{t('in.vault_amount_label')}</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      dir="ltr"
                      lang="en-US"
                      value={formatInputValue(v.vaultAmount)}
                      onChange={(e) => {
                        const val = cleanDecimal(e.target.value);
                        p.onChange({ vaultAmount: val, itemName: t('in.item_usd_text', { qty: val }), ...(val !== '' ? { basePrice: val } : {}) });
                      }}
                      placeholder="1000"
                      className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:border-emerald-500 font-mono text-left"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-2">{t('in.fx_pick_label')}</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                    {p.currencies.map((c) => {
                      const isSelected = v.currencyCode === c.code;
                      return (
                        <button
                          key={c.code}
                          type="button"
                          onClick={() => p.onChange({ currencyCode: c.code, itemName: fxText(c.code, v.fxAmount) })}
                          className={`p-3 rounded-xl border text-right transition-all cursor-pointer relative flex flex-col justify-between ${
                            isSelected ? 'bg-indigo-50 dark:bg-indigo-950/70 border-indigo-500 text-indigo-900 dark:text-indigo-200 ring-2 ring-indigo-500/20 shadow-sm' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-indigo-300'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-black text-xs">{c.nameAr}</span>
                            <span className="font-mono text-[11px] font-bold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{c.symbol}</span>
                          </div>
                          <div className="text-left mt-1">
                            <span className="text-[11px] font-mono font-black text-indigo-600 dark:text-indigo-400" dir="ltr">{c.balanceLabel} {c.symbol}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {selectedFx && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div>
                      <label className={labelCls}>{t('in.fx_amount_label', { symbol: selectedFx.symbol })}</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        dir="ltr"
                        lang="en-US"
                        value={formatInputValue(v.fxAmount)}
                        onChange={(e) => {
                          const val = cleanDecimal(e.target.value);
                          p.onChange({ fxAmount: val, itemName: fxText(v.currencyCode, val) });
                        }}
                        placeholder="500"
                        className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:border-indigo-500 font-mono text-left"
                      />
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-3.5 rounded-xl border border-indigo-200 dark:border-indigo-900/60 flex flex-col justify-center text-xs space-y-1 shadow-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 dark:text-gray-400 font-bold">{t('in.fx_selected')}</span>
                        <span className="font-black text-gray-800 dark:text-gray-200">
                          {selectedFx.nameAr} ({selectedFx.code})
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 dark:text-gray-400 font-bold">{t('in.fx_available')}</span>
                        <span className="font-mono font-black text-indigo-600 dark:text-indigo-400">
                          {selectedFx.balanceLabel} {selectedFx.symbol}
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-1 border-t border-gray-100 dark:border-gray-700 text-[11px]">
                        <span className="text-gray-400 font-bold">{t('in.fx_rate')}</span>
                        <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">{selectedFx.rateLabel}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className={labelCls}>{t('in.vault_name_label')}</label>
              <input type="text" required value={v.itemName} onChange={(e) => p.onChange({ itemName: e.target.value })} placeholder={t('in.vault_name_ph')} className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:border-blue-500" />
            </div>
          </div>
        )}
      </div>

      {/* SECTION 3: FINANCIAL CALCULATIONS & INSTALLMENTS SCHEDULING */}
      <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-800">
        <h3 className="text-sm font-black text-gray-800 dark:text-gray-200 flex items-center gap-2">
          <Calculator size={16} className="text-indigo-500" /> {t('in.calc_section')}
        </h3>

        <div className="flex items-center gap-4">
          <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{t('in.contract_currency')}</span>
          <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
            <button
              type="button"
              disabled={v.itemType !== 'product' || v.productKey !== null}
              onClick={() => p.onChange({ currency: 'IQD' })}
              className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all cursor-pointer disabled:cursor-not-allowed ${cur === 'IQD' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}
            >
              {t('in.currency_iqd')}
            </button>
            <button
              type="button"
              disabled={v.itemType !== 'product' || v.productKey !== null}
              onClick={() => p.onChange({ currency: 'USD' })}
              data-testid="in-currency-usd"
              className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all cursor-pointer disabled:cursor-not-allowed ${cur === 'USD' ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}
            >
              {t('in.currency_usd')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>
              {t('in.base_price', { cur })} <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              inputMode="decimal"
              dir="ltr"
              lang="en-US"
              required
              readOnly={v.itemType === 'foreign_currency'}
              value={formatInputValue(v.basePrice)}
              onChange={(e) => p.onChange({ basePrice: cleanDecimal(e.target.value) })}
              placeholder="0"
              data-testid="in-base-price"
              className={`${inputCls} font-mono text-left`}
            />
            {v.itemType === 'product' && selectedProduct && (
              <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400">{t('in.price_options')}</span>
                {selectedProduct.costPrice ? (
                  <button
                    type="button"
                    onClick={() => p.onChange({ basePrice: selectedProduct.costPrice ?? '' })}
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-md border transition-all cursor-pointer ${
                      v.basePrice === selectedProduct.costPrice ? 'bg-amber-600 text-white border-amber-700 shadow-xs' : 'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700 hover:bg-amber-100'
                    }`}
                  >
                    {t('in.price_cost', { v: formatInputValue(selectedProduct.costPrice) })}
                  </button>
                ) : null}
                {selectedProduct.salePrice ? (
                  <button
                    type="button"
                    onClick={() => p.onChange({ basePrice: selectedProduct.salePrice ?? '' })}
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-md border transition-all cursor-pointer ${
                      v.basePrice === selectedProduct.salePrice ? 'bg-blue-600 text-white border-blue-700 shadow-xs' : 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    {t('in.price_sale', { v: formatInputValue(selectedProduct.salePrice) })}
                  </button>
                ) : null}
              </div>
            )}
          </div>

          <div>
            <label className={labelCls}>
              {t('in.profit_pct')} <span className="text-rose-500">*</span>
            </label>
            <input type="text" inputMode="decimal" dir="ltr" lang="en-US" required value={v.profitPct} onChange={(e) => p.onChange({ profitPct: cleanDecimal(e.target.value) })} placeholder="20" data-testid="in-profit-pct" className={`${inputCls} font-mono text-left`} />
          </div>

          <div className="bg-indigo-50/80 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800/80 rounded-xl p-3.5 flex flex-col justify-between shadow-xs" data-testid="in-price-box">
            <div>
              <span className="text-[11px] font-bold text-indigo-700 dark:text-indigo-300 block mb-0.5">{t('in.price_after_profit', { pct: v.profitPct || '0' })}</span>
              <span className="text-xl font-black text-indigo-900 dark:text-indigo-100 block font-mono" dir="ltr">
                {p.figures?.totalLabel ?? `0 ${cur}`}
              </span>
            </div>
            <div className="mt-2 pt-2 border-t border-indigo-200/80 dark:border-indigo-800/80 space-y-1.5">
              {p.figures?.additionalLabel ? (
                <>
                  <div className="flex items-center justify-between text-xs font-bold text-indigo-900 dark:text-indigo-200">
                    <span className="text-[11px]">{t('in.profit_pct_of_cost', { pct: v.profitPct || '0' })}</span>
                    <span className="font-mono text-emerald-600 dark:text-emerald-400 font-black" dir="ltr">+{p.figures.percentageLabel}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs font-bold text-indigo-900 dark:text-indigo-200">
                    <span className="text-[11px]">{t('in.profit_additional')}</span>
                    <span className="font-mono text-amber-600 dark:text-amber-400 font-black" dir="ltr">+{p.figures.additionalLabel}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs font-bold text-indigo-950 dark:text-indigo-100 bg-emerald-500/10 px-2 py-1 rounded">
                    <span className="text-[11px] font-black">{t('in.profit_total')}</span>
                    <span className="font-mono text-emerald-600 dark:text-emerald-400 font-black" dir="ltr">+{p.figures.profitLabel}</span>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between text-xs font-bold text-indigo-900 dark:text-indigo-200">
                  <span className="text-[11px]">{t('in.profit_amount', { pct: v.profitPct || '0' })}</span>
                  <span className="font-mono text-emerald-600 dark:text-emerald-400 font-black" dir="ltr">+{p.figures?.profitLabel ?? `0 ${cur}`}</span>
                </div>
              )}
              {p.figures?.costLabel ? (
                <div className="pt-1.5 border-t border-dashed border-indigo-200 dark:border-indigo-800 space-y-1 bg-indigo-100/60 dark:bg-indigo-900/40 p-2 rounded-lg">
                  <div className="flex items-center justify-between text-[11px] font-bold text-gray-700 dark:text-gray-300">
                    <span>{t('in.product_cost')}</span>
                    <span className="font-mono font-black text-gray-900 dark:text-white" dir="ltr">{p.figures.costLabel}</span>
                  </div>
                  {p.figures.profitVsCostLabel && (
                    <div className="flex items-center justify-between text-[11px] font-black text-purple-800 dark:text-purple-300">
                      <span>{t('in.pct_of_cost')}</span>
                      <span className="font-mono font-black bg-purple-100 dark:bg-purple-950/80 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded" dir="ltr">{p.figures.profitVsCostLabel}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-[10px] text-indigo-600 dark:text-indigo-400 font-mono" dir="ltr">{t('in.net_profit_hint', { v: p.figures?.netProfitLabel ?? `0 ${cur}` })}</div>
              )}
            </div>
          </div>

          <div>
            <label className={labelCls}>{t('in.down_payment', { cur })}</label>
            <input type="text" inputMode="decimal" dir="ltr" lang="en-US" value={formatInputValue(v.downPayment)} onChange={(e) => p.onChange({ downPayment: cleanDecimal(e.target.value) })} placeholder="0" data-testid="in-down-payment" className={`${inputCls} font-mono text-left`} />
          </div>
        </div>

        {hasDown && (
          <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 p-4 rounded-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <div>
              <h4 className="text-xs font-black text-emerald-800 dark:text-emerald-300">{t('in.down_box_title', { v: `${formatInputValue(v.downPayment)} ${cur}` })}</h4>
              <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-0.5">{t('in.down_box_sub')}</p>
            </div>
            <CustomSelect options={[...p.downPaymentBoxes]} value={v.downPaymentAccountId ?? ''} onChange={(id) => p.onChange({ downPaymentAccountId: String(id) })} placeholder={t('in.box_pick_ph')} searchable={false} className="max-w-xs" />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>
              {t('in.period_label')} <span className="text-rose-500">*</span>
            </label>
            <CustomSelect
              options={[
                { id: 'monthly', label: t('in.period_monthly_opt') },
                { id: 'weekly', label: t('in.period_weekly_opt') },
                { id: 'daily', label: t('in.period_daily_opt') },
              ]}
              value={v.period}
              onChange={(val) => p.onChange({ period: val as ContractPeriod })}
              searchable={false}
            />
          </div>
          <div>
            <label className={labelCls}>
              {t('in.count_label')} <span className="text-rose-500">*</span>
            </label>
            <input type="text" inputMode="numeric" dir="ltr" lang="en-US" required value={v.count} onChange={(e) => p.onChange({ count: toEnglishDigits(e.target.value).replace(/[^0-9]/g, '') })} placeholder="6" data-testid="in-count" className={`${inputCls} font-mono text-left`} />
          </div>
          <div>
            <label className={labelCls}>{t('in.deduction_label')}</label>
            <input type="text" inputMode="decimal" dir="ltr" lang="en-US" value={v.deductionPct} onChange={(e) => p.onChange({ deductionPct: cleanDecimal(e.target.value) })} placeholder="0" className={`${inputCls} font-mono text-left`} />
            {p.figures && v.deductionPct !== '' && Number(v.deductionPct) > 0 && (
              <span className="text-[10px] text-rose-600 dark:text-rose-400 font-mono mt-1 block" dir="ltr">{t('in.deduction_amount', { v: p.figures.deductionLabel })}</span>
            )}
          </div>
          <div>
            <label className={labelCls}>
              {t('in.first_due_label')} <span className="text-rose-500">*</span>
            </label>
            <EnglishDatePicker value={v.firstDueDate} onChange={(d) => p.onChange({ firstDueDate: d })} required />
          </div>
        </div>

        {p.previewError && (
          <div className="p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded-xl text-xs font-bold text-rose-700 dark:text-rose-300 flex items-center gap-2" data-testid="in-preview-error">
            <AlertTriangle size={14} /> {p.previewError}
          </div>
        )}

        <div className={`bg-gradient-to-r from-gray-900 to-slate-800 text-white rounded-2xl p-5 shadow-md grid grid-cols-2 md:grid-cols-4 gap-4 ${p.previewing ? 'opacity-70' : ''}`} data-testid="in-calc-box">
          <div>
            <span className="text-[11px] text-gray-400 block font-semibold">{t('in.calc_remaining')}</span>
            <span className="text-xl font-black text-amber-400 font-mono" dir="ltr">{p.figures?.remainingLabel ?? `0 ${cur}`}</span>
          </div>
          <div>
            <span className="text-[11px] text-gray-400 block font-semibold">{t('in.calc_installment')}</span>
            <span className="text-xl font-black text-emerald-400 font-mono" dir="ltr">{p.figures?.installmentLabel ?? `0 ${cur}`}</span>
          </div>
          <div>
            <span className="text-[11px] text-gray-400 block font-semibold">{t('in.calc_net_profit')}</span>
            <span className={`text-xl font-black font-mono ${p.figures?.netNegative ? 'text-rose-400' : 'text-purple-400'}`} dir="ltr">{p.figures?.netProfitLabel ?? `0 ${cur}`}</span>
          </div>
          <div>
            <span className="text-[11px] text-gray-400 block font-semibold">{t('in.calc_deduction', { pct: v.deductionPct !== '' && Number(v.deductionPct) > 0 ? `(${v.deductionPct}%)` : '' })}</span>
            <span className="text-xl font-black text-rose-400 font-mono" dir="ltr">{p.figures?.deductionLabel ?? `0 ${cur}`}</span>
          </div>
        </div>

        <div>
          <label className={labelCls}>{t('in.note_label')}</label>
          <textarea rows={2} value={v.note} onChange={(e) => p.onChange({ note: e.target.value })} placeholder={t('in.note_ph')} className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-xs font-bold text-gray-900 dark:text-white focus:outline-none focus:border-blue-500" />
        </div>

        {p.figures && p.figures.schedule.length > 0 && (
          <div className="space-y-3 pt-2" data-testid="in-schedule-preview">
            <h4 className="text-xs font-black text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
              <Calendar size={15} className="text-blue-500" /> {t('in.schedule_preview', { n: p.figures.schedule.length })}
            </h4>
            <div className="max-h-44 overflow-y-auto border border-gray-200 dark:border-gray-800 rounded-xl divide-y divide-gray-100 dark:divide-gray-800">
              {p.figures.schedule.map((s) => (
                <div key={s.no} className="p-2.5 px-4 text-xs flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/40">
                  <span className="font-bold text-gray-700 dark:text-gray-300">{t('in.schedule_row', { n: s.no, m: p.figures!.schedule.length })}</span>
                  <span className="font-semibold text-gray-500">
                    {t('in.due_date_label')} <b className="text-blue-600 dark:text-blue-400 font-mono" dir="ltr">{s.dueDate}</b>
                  </span>
                  <span className="font-black text-gray-900 dark:text-white font-mono" dir="ltr">{s.amountLabel}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
        <button type="button" onClick={p.onCancel} className="px-5 py-3 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all cursor-pointer">
          {t('in.cancel')}
        </button>
        <button
          type="submit"
          disabled={Boolean(v.customerMode === 'new' && p.duplicate) || p.busy}
          data-testid="in-submit"
          className="px-7 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-black transition-all shadow-lg hover:shadow-emerald-600/25 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center gap-2"
        >
          <CheckCircle2 size={18} /> {t('in.submit')}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Contract detail (IV:2862-3067)
// ---------------------------------------------------------------------------

export interface ContractDetailRow {
  readonly no: number;
  readonly dueDate: string;
  /** The row amount (plain decimal string, for the inline edit). */
  readonly amount: string;
  readonly amountLabel: string;
  readonly status: 'unpaid' | 'partial' | 'paid';
  readonly overdue: boolean;
  readonly paidLabel: string;
  readonly openLabel: string;
  /** Open amount (plain decimal string) for the payment modal's pre-fill. */
  readonly open: string;
}

export interface ContractDetailPayment {
  readonly operationId: string;
  readonly amountLabel: string;
  readonly dateLabel: string;
  readonly boxName: string;
  readonly operator: string;
  readonly cancelled: boolean;
  readonly receiptNumber: string | null;
}

export interface ContractDetailModalProps {
  readonly contractNumber: string;
  readonly customer: string;
  readonly itemName: string;
  readonly totalLabel: string;
  readonly paidLabel: string;
  readonly remainingLabel: string;
  readonly statusNote: string | null;
  readonly rows: readonly ContractDetailRow[];
  readonly payments: readonly ContractDetailPayment[];
  readonly pendingNote: string | null;
  readonly canEdit: boolean;
  readonly canCollect: boolean;
  readonly canCancel: boolean;
  readonly busy: boolean;
  readonly onSaveRow: (no: number, amount: string) => void;
  readonly onRedistribute: () => void;
  readonly onPay: (row: ContractDetailRow) => void;
  readonly onCancelContract: () => void;
  readonly onCancelPayment: (operationId: string) => void;
  readonly onPrintReceipt: (operationId: string) => void;
  readonly onPrint: () => void;
  readonly onPdf: () => void;
  readonly onShare: () => void;
  readonly onClose: () => void;
}

export function ContractDetailModal(p: ContractDetailModalProps) {
  const { t } = useI18n();
  const [editing, setEditing] = useState<number | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const titles = { print: t('in.capsule_print'), pdf: t('in.capsule_pdf'), share: t('in.capsule_share') };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="in-detail">
      <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-scale-up">
        <div className="p-6 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <div>
            <span className="text-xs font-black text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950 px-2.5 py-1 rounded-md">{t('in.contract_label', { n: p.contractNumber })}</span>
            <h3 className="text-lg font-black text-gray-900 dark:text-white mt-1">{t('in.detail_title', { name: p.customer })}</h3>
          </div>
          <div className="flex items-center gap-2">
            <PrintShareCapsule onPrint={p.onPrint} onPdf={p.onPdf} onShare={p.onShare} titles={titles} testId="in-detail-capsule" />
            {p.canEdit && (
              <button
                type="button"
                disabled={p.busy}
                onClick={p.onRedistribute}
                data-testid="in-redistribute"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-950/60 hover:bg-blue-100 text-blue-700 dark:text-blue-300 rounded-xl text-xs font-bold transition-all cursor-pointer border border-blue-200 dark:border-blue-800"
                title={t('in.redistribute_title')}
              >
                <RefreshCw size={13} /> {t('in.redistribute')}
              </button>
            )}
            {p.canCancel && (
              <button
                type="button"
                onClick={p.onCancelContract}
                data-testid="in-detail-cancel"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 dark:bg-rose-950/60 hover:bg-rose-100 text-rose-700 dark:text-rose-300 rounded-xl text-xs font-bold transition-all cursor-pointer border border-rose-200 dark:border-rose-800"
                title={t('in.cancel_contract_title')}
              >
                <Trash2 size={13} /> {t('in.cancel_contract')}
              </button>
            )}
            <button onClick={p.onClose} className="p-2 rounded-xl text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all cursor-pointer">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {p.statusNote && <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl text-xs font-bold text-amber-800 dark:text-amber-200">{p.statusNote}</div>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 dark:bg-gray-800/40 p-4 rounded-2xl border border-gray-200 dark:border-gray-800">
            <div>
              <span className="text-[11px] text-gray-400 block font-semibold">{t('in.detail_item')}</span>
              <span className="text-xs font-black text-gray-800 dark:text-gray-200">{p.itemName}</span>
            </div>
            <div>
              <span className="text-[11px] text-gray-400 block font-semibold">{t('in.detail_total')}</span>
              <span className="text-xs font-black text-blue-600 dark:text-blue-400">{p.totalLabel}</span>
            </div>
            <div>
              <span className="text-[11px] text-gray-400 block font-semibold">{t('in.detail_paid')}</span>
              <span className="text-xs font-black text-emerald-600 dark:text-emerald-400" data-testid="in-detail-paid">{p.paidLabel}</span>
            </div>
            <div>
              <span className="text-[11px] text-gray-400 block font-semibold">{t('in.detail_remaining')}</span>
              <span className="text-xs font-black text-amber-600 dark:text-amber-400" data-testid="in-detail-remaining">{p.remainingLabel}</span>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-black text-gray-800 dark:text-gray-200 mb-3 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Calendar size={16} className="text-blue-500" /> {t('in.detail_schedule')}
              </span>
              {p.canEdit && <span className="text-[11px] font-semibold text-gray-500">{t('in.detail_edit_hint')}</span>}
            </h4>
            <div className="border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
              {p.rows.map((r) => {
                const isPaid = r.status === 'paid';
                return (
                  <div
                    key={r.no}
                    data-testid="in-detail-row"
                    className={`p-3.5 px-5 flex items-center justify-between gap-4 text-xs font-bold ${isPaid ? 'bg-emerald-50/40 dark:bg-emerald-950/20' : r.overdue ? 'bg-rose-50/40 dark:bg-rose-950/20' : 'bg-white dark:bg-gray-900'}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center font-black text-[11px] ${isPaid ? 'bg-emerald-500 text-white' : r.overdue ? 'bg-rose-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>{r.no}</span>
                      <div>
                        <span className="block text-gray-900 dark:text-white font-black">{t('in.row_number', { n: r.no })}</span>
                        <span className="text-[11px] text-gray-500">
                          {t('in.due_date_label')} <b className="text-gray-700 dark:text-gray-300">{r.dueDate}</b>
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {editing === r.no ? (
                        <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-950/60 p-1.5 rounded-xl border border-blue-200 dark:border-blue-800">
                          <input
                            type="text"
                            inputMode="decimal"
                            dir="ltr"
                            value={formatInputValue(editAmount)}
                            onChange={(e) => setEditAmount(cleanDecimal(e.target.value))}
                            className="w-24 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-xs font-mono font-bold text-gray-900 dark:text-white focus:outline-none"
                            placeholder={t('in.amount_ph')}
                            data-testid="in-row-edit-input"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (editAmount !== '') {
                                p.onSaveRow(r.no, editAmount);
                                setEditing(null);
                              }
                            }}
                            data-testid="in-row-edit-save"
                            className="p-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-all cursor-pointer"
                            title={t('in.row_edit_save')}
                          >
                            <Check size={14} />
                          </button>
                          <button type="button" onClick={() => setEditing(null)} className="p-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-bold transition-all cursor-pointer" title={t('in.cancel')}>
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-black text-gray-900 dark:text-white font-mono" dir="ltr">{r.amountLabel}</span>
                          {r.status === 'unpaid' && p.canEdit && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditing(r.no);
                                setEditAmount(r.amount);
                              }}
                              data-testid="in-row-edit"
                              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/50 rounded-lg transition-all cursor-pointer"
                              title={t('in.row_edit')}
                            >
                              <Edit2 size={13} />
                            </button>
                          )}
                        </div>
                      )}
                      {isPaid ? (
                        <span className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 px-3 py-1 rounded-full text-[11px] font-black flex items-center gap-1">
                          <CheckCircle2 size={13} /> {t('in.row_paid', { v: r.paidLabel })}
                        </span>
                      ) : (
                        <div className="flex items-center gap-2">
                          {r.status === 'partial' && (
                            <span className="bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300 px-3 py-1 rounded-full text-[11px] font-black" data-testid="in-row-partial">{t('in.row_partial', { v: r.openLabel })}</span>
                          )}
                          {p.canCollect && (
                            <button onClick={() => p.onPay(r)} data-testid="in-row-pay" className="bg-blue-600 hover:bg-blue-700 text-white px-3.5 py-1.5 rounded-xl text-xs font-black transition-all shadow-sm cursor-pointer">
                              {t('in.row_pay')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {(p.payments.length > 0 || p.pendingNote) && (
            <div data-testid="in-detail-payments">
              <h4 className="text-sm font-black text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                <CreditCard size={16} className="text-emerald-500" /> {t('in.payments_title')}
              </h4>
              {p.pendingNote && <div className="mb-2 p-2.5 bg-blue-50/60 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/40 rounded-xl text-[11px] text-blue-700 dark:text-blue-300 font-semibold">{p.pendingNote}</div>}
              <div className="border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
                {p.payments.map((x) => (
                  <div key={x.operationId} className={`p-3 px-5 flex items-center justify-between gap-3 text-xs font-bold ${x.cancelled ? 'opacity-50 line-through' : ''}`} data-testid="in-payment-row">
                    <div>
                      <span className="block text-gray-900 dark:text-white font-black font-mono" dir="ltr">{x.amountLabel}</span>
                      <span className="text-[11px] text-gray-500">
                        {x.dateLabel} — {x.boxName} — {x.operator}
                        {x.receiptNumber ? ` — ${t('in.receipt_no', { n: x.receiptNumber })}` : ''}
                      </span>
                    </div>
                    {!x.cancelled && (
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => p.onPrintReceipt(x.operationId)} className="p-1.5 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 rounded-lg cursor-pointer" title={t('in.receipt_print')}>
                          <Printer size={14} />
                        </button>
                        {p.canCancel && (
                          <button type="button" onClick={() => p.onCancelPayment(x.operationId)} data-testid="in-payment-cancel" className="p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg cursor-pointer" title={t('in.cancel_payment')}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 bg-gray-50 dark:bg-gray-800/60 border-t border-gray-100 dark:border-gray-800 flex justify-end">
          <button onClick={p.onClose} className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-xl text-xs font-bold cursor-pointer hover:opacity-80">
            {t('in.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payment modal (IV:3069-3215)
// ---------------------------------------------------------------------------

export interface PaymentModalProps {
  readonly customer: string;
  readonly rowNo: number;
  readonly rowAmountLabel: string;
  readonly amount: string;
  readonly onAmount: (v: string) => void;
  readonly boxes: readonly SelectOption[];
  readonly boxId: string | null;
  readonly onBox: (id: string) => void;
  readonly note: string;
  readonly onNote: (v: string) => void;
  /** The server's preview: what remains after this payment, or that it completes the contract (D-108). */
  readonly previewInfo: { readonly remainingAfterLabel: string; readonly completed: boolean } | null;
  readonly previewError: string | null;
  readonly busy: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

export function PaymentModal(p: PaymentModalProps) {
  const { t } = useI18n();
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="in-payment">
      <div className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 max-w-md w-full p-6 shadow-2xl space-y-5 animate-scale-up">
        <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-base font-black text-gray-900 dark:text-white flex items-center gap-2">
            <CreditCard size={18} className="text-emerald-500" /> {t('in.pay_title', { name: p.customer })}
          </h3>
          <button onClick={p.onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-xl text-xs space-y-1">
            <span className="block text-gray-500">
              {t('in.pay_row')} <b>{p.rowNo}</b>
            </span>
            <span className="block text-gray-500">
              {t('in.pay_row_amount')} <b>{p.rowAmountLabel}</b>
            </span>
          </div>
          <div>
            <label className={labelCls}>{t('in.pay_amount')}</label>
            <input type="text" inputMode="decimal" dir="ltr" value={formatInputValue(p.amount)} onChange={(e) => p.onAmount(cleanDecimal(e.target.value))} data-testid="in-pay-amount" className={inputCls} />
            <div className="space-y-2 mt-2.5">
              {p.previewInfo && (
                <div className={`p-3 border rounded-xl text-xs space-y-1 ${p.previewInfo.completed ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200' : 'bg-blue-50/60 dark:bg-blue-950/30 border-blue-100 dark:border-blue-900/40 text-blue-700 dark:text-blue-300'}`} data-testid="in-pay-preview">
                  <span className="font-black block flex items-center gap-1">
                    <Sparkles size={14} />
                    {p.previewInfo.completed ? t('in.pay_completes') : t('in.pay_oldest_first')}
                  </span>
                  <p className="text-[11px] leading-relaxed">{t('in.pay_remaining_after', { v: p.previewInfo.remainingAfterLabel })}</p>
                </div>
              )}
              {p.previewError && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl text-xs text-amber-800 dark:text-amber-200 font-bold flex items-center gap-1" data-testid="in-pay-error">
                  <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400" /> {p.previewError}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className={labelCls}>{t('in.pay_box')}</label>
            <CustomSelect options={[...p.boxes]} value={p.boxId ?? ''} onChange={(id) => p.onBox(String(id))} placeholder={t('in.box_pick_ph')} searchable={false} />
          </div>
          <div>
            <label className={labelCls}>{t('in.pay_note')}</label>
            <input type="text" value={p.note} onChange={(e) => p.onNote(e.target.value)} placeholder={t('in.pay_note_ph')} className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-xs font-bold text-gray-900 dark:text-white focus:outline-none focus:border-blue-500" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-800">
          <button type="button" onClick={p.onClose} className="px-4 py-2.5 rounded-xl border border-gray-200 text-xs font-bold text-gray-600 cursor-pointer">
            {t('in.cancel')}
          </button>
          <button type="button" disabled={p.busy || !p.previewInfo} onClick={p.onConfirm} data-testid="in-pay-confirm" className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black cursor-pointer shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
            {t('in.pay_confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cancel dialogs (the old delete modal's frame, IV:3218-3305; reason instead of the super code)
// ---------------------------------------------------------------------------

export interface ReasonDialogProps {
  readonly title: string;
  readonly warning: ReactNode;
  readonly confirmLabel: string;
  readonly busy: boolean;
  readonly onConfirm: (reason: string) => void;
  readonly onClose: () => void;
}

export function ReasonDialog(p: ReasonDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn" data-testid="in-reason-dialog">
      <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 max-w-md w-full shadow-2xl border border-rose-200 dark:border-rose-900/50 space-y-5">
        <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3 text-rose-600 dark:text-rose-400 font-bold text-lg">
            <div className="p-2.5 bg-rose-100 dark:bg-rose-950/80 rounded-2xl">
              <ShieldAlert size={22} />
            </div>
            <h3>{p.title}</h3>
          </div>
          <button type="button" onClick={p.onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all cursor-pointer">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-4">
          <div className="p-4 bg-rose-50/80 dark:bg-rose-950/40 rounded-2xl border border-rose-200 dark:border-rose-900/50 text-xs text-rose-900 dark:text-rose-200 leading-relaxed">
            <p className="font-bold text-sm mb-1 text-rose-900 dark:text-rose-100 flex items-center gap-1.5">
              <AlertTriangle size={16} /> {t('in.cancel_warning_head')}
            </p>
            {p.warning}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (reason.trim()) p.onConfirm(reason.trim());
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-2">{t('in.cancel_reason')}</label>
              <textarea
                autoFocus
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('in.cancel_reason_ph')}
                data-testid="in-reason"
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-rose-500 outline-none text-gray-900 dark:text-white placeholder:text-gray-400 placeholder:font-normal"
              />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button type="button" onClick={p.onClose} className="flex-1 py-3 px-4 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl text-xs font-bold transition-all cursor-pointer">
                {t('in.cancel')}
              </button>
              <button
                type="submit"
                disabled={p.busy || !reason.trim()}
                data-testid="in-reason-confirm"
                className="flex-1 py-3 px-4 bg-rose-600 hover:bg-rose-700 active:scale-95 text-white rounded-2xl text-xs font-bold shadow-lg shadow-rose-600/30 transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 size={16} /> {p.confirmLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
